import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, downloadFile } from "../api/client";
import type { Project, SpecCheck as Check, SpecFile, SpecResult } from "../api/types";
import { Icon } from "../components/icons";
import { RunPanel } from "../components/RunPanel";
import { ProjectHeader } from "../components/ProjectHeader";
import { toast } from "../components/toast";

// Delivery spec check (audio_analysis module): measure every uploaded audio
// file with ffmpeg server-side and grade it against a platform profile
// (Audible UK 2025). Verdicts: pass / warn (outside recommended, inside
// accepted) / fail (outside accepted — the platform may reject).

const COLUMNS: { rule: string; head: string }[] = [
  { rule: "naming", head: "Name" },
  { rule: "silence_head", head: "Silence start" },
  { rule: "silence_tail", head: "Silence end" },
  { rule: "container", head: "Format" },
  { rule: "channels", head: "Mono/Stereo" },
  { rule: "duration", head: "Length" },
  { rule: "sample_rate", head: "Rate" },
  { rule: "bit_depth", head: "Bits" },
  { rule: "lufs", head: "LUFS" },
  { rule: "true_peak", head: "True peak" },
  { rule: "lra", head: "LRA" },
  { rule: "noise_floor", head: "Noise Floor" },
];

function cellFor(f: SpecFile, rule: string) {
  const c = f.checks.find((x) => x.rule === rule);
  if (!c) return <td key={rule} className="sc-cell">—</td>;
  const text = rule === "naming" ? (c.status === "pass" ? "Yes" : c.status === "warn" ? "Prefix OK" : "No") : c.value;
  return (
    <td key={rule} className={`sc-cell st-${c.status}`} title={`${c.label} — target ${c.target}${c.note ? ` · ${c.note}` : ""}`}>
      {text}
    </td>
  );
}

function VerdictChip({ status, children }: { status: string; children: React.ReactNode }) {
  return <span className={`sc-chip st-${status}`}>{children}</span>;
}

function uniColor(score: number): string {
  return score >= 75 ? "var(--ok)" : score >= 55 ? "var(--warn)" : "var(--danger)";
}

export function SpecCheck() {
  const { projectId } = useParams();
  const qc = useQueryClient();
  const [jobId, setJobId] = useState<string | null>(null);

  const [productId, setProductId] = useState("");

  const [profileId, setProfileId] = useState("audible_uk_2025");
  const [deliverableSet, setDeliverableSet] = useState("stereo");
  const isAtmos = profileId === "audible_uk_2025_atmos_downmix";
  const [soundDesign, setSoundDesign] = useState<"unknown" | "yes" | "no">("unknown");
  const [assembly, setAssembly] = useState("One chapter per file");
  const [prefilled, setPrefilled] = useState(false);
  const [showTargets, setShowTargets] = useState(false);
  // Append the manual Table-of-Contents scoring tab to the Excel export. The
  // choice lives in a popover that opens from the "Excel report" button (so the
  // option is self-explanatory, not a bare checkbox the user has to decode).
  const [includeToc, setIncludeToc] = useState(false);
  const [xlsxMenu, setXlsxMenu] = useState(false);

  const { data: project } = useQuery({
    queryKey: ["project", projectId],
    queryFn: () => api<Project>(`/api/projects/${projectId}`),
    refetchInterval: jobId ? 2500 : false,
  });

  // Reattach to a spec-check already running on this project (e.g. after a
  // refresh); ignore other modules' jobs so we don't hijack a proof run's log.
  useEffect(() => {
    // Its own job, even while a proof runs beside it.
    const aj = (project?.active_jobs ?? (project?.active_job ? [project.active_job] : []))
      .find((j) => j.module_id === "audio_analysis");
    if (aj) setJobId(aj.id);
  }, [project?.active_jobs?.map((j) => j.id).join(","), project?.active_job?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const { data: result, isError: noResult } = useQuery({
    queryKey: ["spec", projectId],
    queryFn: () => api<SpecResult>(`/api/modules/audio_analysis/projects/${projectId}/spec`),
    retry: false,
  });
  // The files an out-of-date report is about, once each by the name a person
  // knows them by (older servers sent only the storage names).
  const staleNames = result?.stale
    ? result.stale.files ?? [...result.stale.not_measured, ...result.stale.no_longer_used]
    : [];

  // Prefill once: the Product ID from the project's canonical value (provided
  // from either the spec check or the Tracking View essentials), the rest from
  // the last run's params. Waits for the project + the last result (or its 404).
  useEffect(() => {
    if (prefilled || !project) return;
    if (result === undefined && !noResult) return;
    setProductId((project.product_id || result?.params.product_id || "").trim());
    setSoundDesign(result?.params.sound_design === true ? "yes" : result?.params.sound_design === false ? "no" : "unknown");
    if (result?.params.assembly_type) setAssembly(result.params.assembly_type);
    // Restore the spec that was last RUN, not the first one in the list. Coming
    // back to an Atmos project and finding the form set to Non-Atmos invites
    // re-running it against the wrong requirements without noticing.
    if (result?.profile?.id) setProfileId(result.profile.id);
    if (result?.params.deliverable_set) setDeliverableSet(result.params.deliverable_set);
    setPrefilled(true);
  }, [project, result, noResult, prefilled]);

  // Persist the Product ID to the project the moment it's entered here — so it's
  // the same canonical value the Tracking View + reports use, not only at run.
  const saveProductId = useMutation({
    mutationFn: (v: string) =>
      api(`/api/projects/${projectId}/product-id`, { method: "POST", body: JSON.stringify({ product_id: v.trim() }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project", projectId] }),
  });

  const start = useMutation({
    mutationFn: () =>
      api<{ job_id: string }>(`/api/projects/${projectId}/jobs`, {
        method: "POST",
        body: JSON.stringify({
          module_id: "audio_analysis",
          job_type: "spec_check",
          params: {
            profile_id: profileId,
            product_id: productId.trim(),
            sound_design: soundDesign === "unknown" ? null : soundDesign === "yes",
            assembly_type: assembly,
            deliverable_set: isAtmos ? deliverableSet : "",
          },
        }),
      }),
    onSuccess: (r) => setJobId(r.job_id),
    onError: (e: any) => toast(e.message || "Could not start the spec check", "error"),
  });

  const onJobDone = () => {
    setJobId(null);
    qc.invalidateQueries({ queryKey: ["spec", projectId] });
    qc.invalidateQueries({ queryKey: ["project", projectId] });
  };

  const counts = result?.project.counts;
  const projChecks = result?.project.checks ?? [];
  // Targets are identical across files for a given rule, so read them off the
  // first readable file to populate the collapsible "Targets & tolerances" row.
  const targetMap: Record<string, string> = {};
  result?.files.find((f) => !f.error)?.checks.forEach((c) => { targetMap[c.rule] = c.target; });
  const totalS = result?.project.total_runtime_s ?? 0;
  const hms = `${String(Math.floor(totalS / 3600)).padStart(2, "0")}:${String(Math.floor((totalS % 3600) / 60)).padStart(2, "0")}:${String(Math.round(totalS % 60)).padStart(2, "0")}`;

  return (
    <>
      <ProjectHeader
        projectId={projectId!}
        name={project?.name ?? "Project"}
        active="audio_analysis"
        right={result ? (
          <>
            <div className="xlsx-export-wrap">
              <button className="btn ghost sm" onClick={() => setXlsxMenu((v) => !v)}
                aria-expanded={xlsxMenu} aria-haspopup="dialog">
                <Icon name="download" size={15} /> Excel report
              </button>
              {xlsxMenu && (
                <>
                  <div className="menu-backdrop" onClick={() => setXlsxMenu(false)} />
                  <div className="xlsx-export-menu" role="dialog" aria-label="Excel report options">
                    <div className="xem-title">Excel report</div>
                    <label className="xem-opt">
                      <input type="checkbox" checked={includeToc}
                        onChange={(e) => setIncludeToc(e.target.checked)} />
                      <span className="xem-opt-text">
                        <b>Include Table of Contents tab</b>
                        <small>Adds a manual ToC scoring tab — a Product-ID check and a Yes/No
                          checklist — for you to fill in.</small>
                      </span>
                    </label>
                    <button className="btn primary sm xem-go"
                      onClick={() => {
                        downloadFile(`/api/modules/audio_analysis/projects/${projectId}/spec/export/xlsx${includeToc ? "?include_toc=1" : ""}`).catch(() => {});
                        setXlsxMenu(false);
                      }}>
                      <Icon name="download" size={15} /> Download
                    </button>
                  </div>
                </>
              )}
            </div>
            <button className="btn ghost sm" onClick={() => downloadFile(`/api/modules/audio_analysis/projects/${projectId}/spec/export/csv`).catch(() => {})}>
              <Icon name="download" size={15} /> CSV
            </button>
          </>
        ) : undefined}
      />

      <div className="page spec-page">
      <div className="card sc-form">
        <div className="sc-form-row">
          <label>
            <span>Profile</span>
            {/* Live now there is a second spec to pick. The Atmos entry is for
                the stereo and binaural DOWNMIXES of a spatial production — the
                .mxf master needs the Dolby Renderer, which nothing here can
                read, so it is deliberately not on this list. */}
            <select value={profileId} onChange={(e) => setProfileId(e.target.value)}>
              <option value="audible_uk_2025">Audible UK 2025 — Non-Atmos</option>
              <option value="audible_uk_2025_atmos_downmix">Audible UK 2025 — Atmos downmix (stereo / binaural)</option>
            </select>
          </label>
          {/* Only for Atmos: names the band the files sit under in the report.
              A project is one downmix, because stereo and binaural share
              filenames and ship separately. */}
          {isAtmos && (
            <label>
              <span>Downmix</span>
              <select value={deliverableSet} onChange={(e) => setDeliverableSet(e.target.value)}>
                <option value="stereo">Stereo</option>
                <option value="binaural">Binaural</option>
              </select>
            </label>
          )}
          <label>
            <span>Product ID <em className="muted">(for file-naming checks)</em></span>
            <input
              placeholder="e.g. OR_ORIG_002137"
              value={productId}
              onChange={(e) => setProductId(e.target.value)}
              onBlur={() => { if (productId.trim() !== (project?.product_id || "")) saveProductId.mutate(productId); }}
            />
          </label>
          <label>
            <span>Sound design / music in production?</span>
            <select value={soundDesign} onChange={(e) => setSoundDesign(e.target.value as any)}>
              <option value="unknown">Not declared</option>
              <option value="no">No — voice only (mono expected)</option>
              <option value="yes">Yes (stereo expected)</option>
            </select>
          </label>
          <label>
            <span>File assembly</span>
            <select value={assembly} onChange={(e) => setAssembly(e.target.value)}>
              <option>One chapter per file</option>
              <option>One episode per file</option>
              <option>~30-minute segments</option>
            </select>
          </label>
          <button
            className="btn primary"
            disabled={!!jobId || start.isPending}
            onClick={() => start.mutate()}
          >
            <Icon name="audio-waveform" size={16} />
            {result ? "Re-check files" : "Check files"}
          </button>
        </div>
        <p className="muted sc-hint">
          Measures every uploaded audio file (loudness, true peak, silences, format…) against
          Audible's delivery specifications. Green = pass · amber = outside recommended ·
          red = outside accepted (Audible may reject).
        </p>
      </div>

      {jobId && <RunPanel key={jobId} jobId={jobId} title="Spec check" onDone={onJobDone} />}

      {!result && !jobId && (
        <div className="empty-state">
          <Icon name="audio-waveform" size={36} className="muted" />
          <h3>No spec check yet</h3>
          <p className="muted">
            {noResult ? "Run the first check to grade this project's audio files against the Audible spec." : "Loading…"}
          </p>
        </div>
      )}

      {result?.stale && (
        <div className="card sc-stale">
          <Icon name="alert" size={16} />
          <div>
            <strong>This report describes a different set of files</strong>
            <p>
              {result.stale.not_measured.length > 0 && (
                <>{result.stale.not_measured.length} file
                  {result.stale.not_measured.length === 1 ? " is" : "s are"} now official and
                  {result.stale.not_measured.length === 1 ? " was" : " were"} never measured
                  {result.stale.no_longer_used.length > 0 ? "; " : ". "}</>
              )}
              {result.stale.no_longer_used.length > 0 && (
                <>{result.stale.no_longer_used.length} measured file
                  {result.stale.no_longer_used.length === 1 ? " is" : "s are"} no longer the version
                  the project uses. </>
              )}
              The numbers below still stand for what they measured - nothing has been
              re-run, because that is your call.
            </p>
            <span className="sc-stale-names">
              {staleNames.slice(0, 6).join(", ")}
              {staleNames.length > 6 ? " …" : ""}
            </span>
          </div>
          <button className="btn sm primary" disabled={start.isPending || !!jobId}
            onClick={() => start.mutate()}>Run it again</button>
        </div>
      )}

      {result && (
        <>
          <div className="card sc-summary">
            <VerdictChip status={result.project.verdict}>
              {counts?.fail ? `${counts.fail} FAIL` : counts?.warn ? `${counts.warn} WARNINGS` : "ALL PASS"}
            </VerdictChip>
            <span className="muted">
              {result.files.length} files · total runtime {hms} · checked {result.generated_at.slice(0, 16).replace("T", " ")}
            </span>
            {/* What this verdict actually means. The form's dropdown says what
                the NEXT check would use; it said nothing about the one on
                screen, and defaulted to the first spec in the list — so a
                result measured against the Atmos spec sat under a control
                reading "Non-Atmos". */}
            <span className="sc-measured" title="The requirements these files were graded against">
              <Icon name="check" size={13} />
              Measured against <b>{result.profile.label}</b>
              {result.params.deliverable_set
                ? ` · ${result.params.deliverable_set === "binaural" ? "Binaural" : "Stereo"} downmix`
                : ""}
            </span>
            <button
              className="btn ghost sm sc-targets-toggle"
              aria-expanded={showTargets}
              onClick={() => setShowTargets((v) => !v)}
            >
              <Icon name={showTargets ? "chevron-down" : "chevron-right"} size={14} />
              Targets &amp; tolerances
            </button>
          </div>

          {result.project.spec_rollup && result.project.spec_rollup.length > 0 && (
            <div className="sc-rollup">
              {result.project.spec_rollup.map((r) => (
                <span key={r.label} className={`sc-roll ${r.uniform ? "ok" : "warn"}`}>
                  <span className="sc-roll-label">{r.label}</span> {r.value}
                </span>
              ))}
            </div>
          )}

          <div className="sc-section-label">By metric</div>
          <div className="sc-chips">
            {(result.project.by_metric ?? []).map((b) => (
              <span key={b.key} className={`sc-chip sm st-${b.status}`}>
                {b.label} · {b.status === "pass" ? "all pass" : b.fail ? `${b.fail} to fix` : `${b.warn} warning${b.warn > 1 ? "s" : ""}`}
              </span>
            ))}
            {projChecks.map((c: Check) => (
              <span key={c.rule} className={`sc-chip sm st-${c.status}`} title={`${c.label}: target ${c.target}${c.note ? ` · ${c.note}` : ""}`}>
                {c.label}: {c.value}
              </span>
            ))}
          </div>

          {result.project.uniformity?.overall != null && (
            <>
              <div className="sc-section-label">Cross File Consistency</div>
              <div className="card sc-uniformity">
                <div className="sc-uni-score">
                  <div className="sc-uni-num">{result.project.uniformity.overall}</div>
                  <div className="sc-uni-band">/ 100 · {result.project.uniformity.band}</div>
                </div>
                <div className="sc-uni-body">
                  {result.project.uniformity.metrics.filter((m) => m.score != null).map((m) => (
                    <div key={m.key} className="sc-uni-bar" title={m.mean != null ? `mean ${m.mean}, σ ${m.stdev}` : ""}>
                      <span className="sc-uni-lbl">{m.label}</span>
                      <span className="sc-uni-track">
                        <span className="sc-uni-fill" style={{ width: `${m.score}%`, background: uniColor(m.score!) }} />
                      </span>
                      <span className="sc-uni-val">{m.score}</span>
                    </div>
                  ))}
                  {result.project.uniformity.outliers.length > 0 && (
                    <div className="sc-uni-outliers">
                      <span className="muted">Outliers · </span>
                      {result.project.uniformity.outliers.slice(0, 6).map((o, i) => (
                        <span key={i}>
                          {i > 0 ? " · " : ""}
                          <b>{(o.name || o.filename).replace(/\.[^.]+$/, "")}</b> {o.metric} {o.value} ({o.dev > 0 ? "+" : ""}{o.dev})
                        </span>
                      ))}
                      {result.project.uniformity.outliers.length > 6 && (
                        <span> · +{result.project.uniformity.outliers.length - 6} more</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          <div className="card sc-table-wrap">
            <table className="sc-table">
              <thead>
                <tr>
                  <th>File</th>
                  {COLUMNS.map((c) => <th key={c.rule}>{c.head}</th>)}
                  <th>Verdict</th>
                </tr>
                {showTargets && (
                  <tr className="sc-targets-row">
                    <th>Target</th>
                    {COLUMNS.map((c) => <th key={c.rule}>{targetMap[c.rule] || "—"}</th>)}
                    <th></th>
                  </tr>
                )}
              </thead>
              <tbody>
                {result.files.map((f) => (
                  <tr key={f.filename}>
                    <td className="sc-file" title={f.error || f.name || f.filename}>{f.name || f.filename}</td>
                    {f.error ? (
                      <td className="sc-cell st-fail" colSpan={COLUMNS.length}>unreadable: {f.error}</td>
                    ) : (
                      COLUMNS.map((c) => cellFor(f, c.rule))
                    )}
                    <td className={`sc-cell sc-verdict st-${f.verdict}`}>{f.verdict.toUpperCase()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      </div>
    </>
  );
}
